import React, { useState } from 'react'
import { X, Crown, CreditCard, Sparkles, Zap, Star, Check, TrendingUp } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import StripeService from '../../services/stripe'

interface SubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
}

interface SubscriptionOption {
  id: string
  type: 'subscription' | 'credits'
  title: string
  description: string
  price: number
  originalPrice?: number
  value: string
  duration?: string
  popular?: boolean
  bestValue?: boolean
  icon: React.ReactNode
  features: string[]
  savings?: string
  savingsPercent?: number
  priceId: string
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth()
  const [selectedSubscription, setSelectedSubscription] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'select' | 'payment' | 'success'>('select')

  // Monthly base price for calculating savings
  const MONTHLY_BASE_PRICE = 9.99

  const subscriptionOptions: SubscriptionOption[] = [
    {
      id: 'credits_10',
      type: 'credits',
      title: '10 Image Credits',
      description: 'Perfect for trying out premium features',
      price: 4.99,
      originalPrice: 5.99,
      value: '10 generations',
      icon: <Zap className="w-6 h-6 text-yellow-500" />,
      features: [
        '10 high-quality AI generations',
        'Access to all filters and styles',
        'HD quality images',
        'No expiration date',
        'Instant activation'
      ],
      savings: 'Save $1.00',
      savingsPercent: 17,
      priceId: 'price_credits_10'
    },
    {
      id: 'credits_25',
      type: 'credits',
      title: '25 Image Credits',
      description: 'Great for regular users',
      price: 9.99,
      originalPrice: 12.49,
      value: '25 generations',
      icon: <Zap className="w-6 h-6 text-orange-500" />,
      features: [
        '25 high-quality AI generations',
        'Access to all filters and styles',
        'HD quality images',
        'No expiration date',
        'Instant activation',
        'Priority support'
      ],
      savings: 'Save $2.50',
      savingsPercent: 20,
      priceId: 'price_credits_25'
    },
    {
      id: 'credits_50',
      type: 'credits',
      title: '50 Image Credits',
      description: 'Best value for credit packages',
      price: 17.99,
      originalPrice: 24.99,
      value: '50 generations',
      popular: true,
      icon: <Zap className="w-6 h-6 text-purple-500" />,
      features: [
        '50 high-quality AI generations',
        'Access to all filters and styles',
        'HD quality images',
        'No expiration date',
        'Instant activation',
        'Priority support',
        'Exclusive filters'
      ],
      savings: 'Save $7.00',
      savingsPercent: 28,
      priceId: 'price_credits_50'
    },
    {
      id: 'credits_100',
      type: 'credits',
      title: '100 Image Credits',
      description: 'Maximum value credit package',
      price: 29.99,
      originalPrice: 49.99,
      value: '100 generations',
      bestValue: true,
      icon: <Zap className="w-6 h-6 text-green-500" />,
      features: [
        '100 high-quality AI generations',
        'Access to all filters and styles',
        'Ultra HD quality images',
        'No expiration date',
        'Instant activation',
        'Priority support',
        'Exclusive filters',
        'Beta feature access'
      ],
      savings: 'Save $20.00',
      savingsPercent: 40,
      priceId: 'price_credits_100'
    },
    {
      id: 'subscription_1',
      type: 'subscription',
      title: '1 Month Premium',
      description: 'Get started with premium features',
      price: 9.99,
      value: '1 month',
      duration: '30 days',
      icon: <Crown className="w-6 h-6 text-blue-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing queue',
        'All premium filters & styles',
        'HD quality images',
        'Advanced customization',
        'Email support'
      ],
      priceId: 'price_premium_monthly'
    },
    {
      id: 'subscription_3',
      type: 'subscription',
      title: '3 Month Premium',
      description: 'Great value for regular users',
      price: 24.99,
      originalPrice: 29.97,
      value: '3 months',
      duration: '90 days',
      popular: true,
      icon: <Crown className="w-6 h-6 text-purple-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing queue',
        'All premium filters & styles',
        'HD quality images',
        'Advanced customization',
        'Priority support',
        'Early access to features'
      ],
      savings: 'Save $4.98',
      savingsPercent: 17,
      priceId: 'price_premium_3month'
    },
    {
      id: 'subscription_6',
      type: 'subscription',
      title: '6 Month Premium',
      description: 'Perfect for dedicated creators',
      price: 44.99,
      originalPrice: 59.94,
      value: '6 months',
      duration: '180 days',
      icon: <Crown className="w-6 h-6 text-pink-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing queue',
        'All premium filters & styles',
        'Ultra HD quality images',
        'Advanced customization',
        'Priority support',
        'Early access to features',
        'Exclusive beta features'
      ],
      savings: 'Save $14.95',
      savingsPercent: 25,
      priceId: 'price_premium_6month'
    },
    {
      id: 'subscription_12',
      type: 'subscription',
      title: '1 Year Premium',
      description: 'Maximum value and savings',
      price: 79.99,
      originalPrice: 119.88,
      value: '12 months',
      duration: '365 days',
      bestValue: true,
      icon: <Crown className="w-6 h-6 text-gold-500" />,
      features: [
        'Unlimited AI generations',
        'Highest priority processing',
        'All premium + exclusive filters',
        'Ultra HD quality images',
        'Advanced customization',
        'VIP support',
        'Early access to all features',
        'Exclusive beta features',
        'Commercial usage rights',
        'API access'
      ],
      savings: 'Save $39.89',
      savingsPercent: 33,
      priceId: 'price_premium_yearly'
    }
  ]

  const selectedOption = subscriptionOptions.find(option => option.id === selectedSubscription)

  const handleSubscriptionSelection = (subscriptionId: string) => {
    setSelectedSubscription(subscriptionId)
    setStep('payment')
  }

  const handlePayment = async () => {
    if (!selectedOption || !user) return
    
    setIsProcessing(true)
    
    try {
      if (selectedOption.type === 'subscription') {
        // Handle subscription upgrade
        await StripeService.initiateUpgrade(selectedOption.id.replace('subscription_', ''), user.id)
      } else {
        // Handle credit purchase
        await StripeService.initiateCreditPurchase(selectedOption.id, user.id)
      }
      
      setStep('success')
    } catch (error) {
      console.error('Payment failed:', error)
      alert(error instanceof Error ? error.message : 'Payment failed. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const resetModal = () => {
    setStep('select')
    setSelectedSubscription('')
    setIsProcessing(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Upgrade to Premium</h2>
              <p className="text-sm text-gray-600">Unlock unlimited AI generations and premium features</p>
            </div>
          </div>
          <button 
            onClick={() => {
              resetModal()
              onClose()
            }}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' && (
            <div>
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Choose Your Plan</h3>
                <p className="text-gray-600">Select the perfect subscription or credit package for your needs</p>
                <div className="flex items-center justify-center mt-4 space-x-4">
                  <div className="flex items-center text-sm text-green-600">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    <span>Progressive discounts on larger packages</span>
                  </div>
                  <div className="flex items-center text-sm text-blue-600">
                    <Star className="w-4 h-4 mr-1" />
                    <span>Save up to 40% vs individual purchases</span>
                  </div>
                </div>
              </div>

              {/* Credit Packages */}
              <div className="mb-12">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Zap className="w-5 h-5 text-yellow-500 mr-2" />
                  Credit Packages
                  <span className="ml-2 text-sm text-gray-500 font-normal">One-time purchases, never expire</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {subscriptionOptions.filter(option => option.type === 'credits').map((option) => (
                    <div
                      key={option.id}
                      onClick={() => handleSubscriptionSelection(option.id)}
                      className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                        option.popular 
                          ? 'border-purple-500 bg-purple-50' 
                          : option.bestValue
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {option.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      {option.bestValue && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Best Value
                          </span>
                        </div>
                      )}

                      <div className="text-center">
                        <div className="mb-4">
                          {option.icon}
                        </div>
                        
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {option.title}
                        </h4>
                        
                        <p className="text-sm text-gray-600 mb-4">
                          {option.description}
                        </p>
                        
                        <div className="mb-4">
                          <div className="flex items-center justify-center space-x-2">
                            <span className="text-2xl font-bold text-gray-900">
                              ${option.price}
                            </span>
                            {option.originalPrice && (
                              <span className="text-lg text-gray-400 line-through">
                                ${option.originalPrice}
                              </span>
                            )}
                          </div>
                          {option.savings && (
                            <div className="text-sm text-green-600 font-medium mt-1">
                              {option.savings} ({option.savingsPercent}% off)
                            </div>
                          )}
                        </div>

                        <ul className="text-xs text-gray-600 space-y-1 mb-4">
                          {option.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <Check className="w-3 h-3 text-green-500 mr-1 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                          {option.features.length > 3 && (
                            <li className="text-gray-500">
                              +{option.features.length - 3} more features
                            </li>
                          )}
                        </ul>

                        <button className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                          option.popular
                            ? 'bg-purple-500 hover:bg-purple-600 text-white'
                            : option.bestValue
                            ? 'bg-green-500 hover:bg-green-600 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}>
                          Purchase Credits
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscription Plans */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Crown className="w-5 h-5 text-purple-500 mr-2" />
                  Subscription Plans
                  <span className="ml-2 text-sm text-gray-500 font-normal">Recurring subscriptions with unlimited generations</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {subscriptionOptions.filter(option => option.type === 'subscription').map((option) => (
                    <div
                      key={option.id}
                      onClick={() => handleSubscriptionSelection(option.id)}
                      className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                        option.popular 
                          ? 'border-purple-500 bg-purple-50' 
                          : option.bestValue
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {option.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      {option.bestValue && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Best Value
                          </span>
                        </div>
                      )}

                      <div className="text-center">
                        <div className="mb-4">
                          {option.icon}
                        </div>
                        
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {option.title}
                        </h4>
                        
                        <p className="text-sm text-gray-600 mb-4">
                          {option.description}
                        </p>
                        
                        <div className="mb-4">
                          <div className="flex items-center justify-center space-x-2">
                            <span className="text-2xl font-bold text-gray-900">
                              ${option.price}
                            </span>
                            {option.originalPrice && (
                              <span className="text-lg text-gray-400 line-through">
                                ${option.originalPrice}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            for {option.value}
                          </div>
                          {option.savings && (
                            <div className="text-sm text-green-600 font-medium mt-1">
                              {option.savings} ({option.savingsPercent}% off)
                            </div>
                          )}
                        </div>

                        <ul className="text-xs text-gray-600 space-y-1 mb-4">
                          {option.features.slice(0, 4).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <Check className="w-3 h-3 text-green-500 mr-1 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                          {option.features.length > 4 && (
                            <li className="text-gray-500">
                              +{option.features.length - 4} more features
                            </li>
                          )}
                        </ul>

                        <button className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                          option.popular
                            ? 'bg-purple-500 hover:bg-purple-600 text-white'
                            : option.bestValue
                            ? 'bg-green-500 hover:bg-green-600 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}>
                          Subscribe Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value Proposition */}
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Why Choose Toefetti Premium?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      <span className="text-sm text-gray-700">300+ Premium Filters</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Zap className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm text-gray-700">Priority Processing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Crown className="w-5 h-5 text-blue-500" />
                      <span className="text-sm text-gray-700">Unlimited Generations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'payment' && selectedOption && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Your Purchase</h3>
                <p className="text-gray-600">Review and confirm your subscription</p>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">Order Summary</h4>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium">{selectedOption.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">{selectedOption.value}</span>
                  </div>
                  {selectedOption.originalPrice && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Regular Price:</span>
                        <span className="text-gray-400 line-through">${selectedOption.originalPrice}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Savings:</span>
                        <span className="font-medium">{selectedOption.savings}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t pt-3 flex justify-between text-lg font-semibold">
                    <span>Total:</span>
                    <span>${selectedOption.price}</span>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Payment Information
                </h4>
                
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-gray-600 mb-4">
                    Secure payment processing powered by Stripe
                  </p>
                  <p className="text-sm text-gray-500">
                    Your payment information is encrypted and secure
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePayment}
                  disabled={isProcessing}
                  className="flex-1 py-3 px-6 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white rounded-lg transition-colors flex items-center justify-center"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Complete Purchase
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'success' && selectedOption && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-green-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Welcome to Premium! 🎉
              </h3>
              
              <p className="text-gray-600 mb-6">
                Your <strong>{selectedOption.title}</strong> has been activated successfully. 
                You now have access to all premium features and unlimited AI generations.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-700 text-sm">
                  <strong>What's included:</strong><br />
                  • Unlimited AI image generations<br />
                  • Priority processing queue<br />
                  • Access to all premium filters<br />
                  • HD quality downloads<br />
                  • Premium customer support
                </p>
              </div>

              <button
                onClick={() => {
                  resetModal()
                  onClose()
                }}
                className="py-3 px-8 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
              >
                Start Creating
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionModal 